// App shell: top bar (logo, nav, avatar), mobile bottom tab bar, footer.
// Also owns the cross-page glue: the signed-in user context, the notification
// unread badge, the global 401 → re-login hook, and the toast host.

import type { ReactElement, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useA11y, type TextSize } from '../lib/a11y';
import { avatarUrl, getMyNotificationsCount, setUnauthorizedHandler, type NotificationCounts } from '../lib/api';
import { login } from '../lib/auth';
import { hasBilling } from '../lib/billing';
import { config } from '../lib/config';
import { groupKey, useGroupName, useSelectedGroup } from '../lib/group';
import { wordingFor } from '../lib/projectType';
import { revealTheme, useTheme } from '../lib/theme';
import { UserProvider, useUser } from '../lib/user';
import Icon, { type IconName } from './Icon';
import { NotificationFilter, NotificationList, useNotificationFeed } from './NotificationFeed';
import { Drawer, Dropdown, Segmented, Switch, ToastHost } from './ui';

/* ------------------------------------------------ notification badge ctx -- */

type NotifContextValue = {
  counts: NotificationCounts | null;
  refresh: () => void;
};

const NotifContext = createContext<NotifContextValue>({ counts: null, refresh: () => {} });

export function useNotifCounts(): NotifContextValue {
  return useContext(NotifContext);
}

function NotifProvider({ children }: { children: ReactNode }): ReactElement {
  const [counts, setCounts] = useState<NotificationCounts | null>(null);

  const refresh = useCallback(() => {
    getMyNotificationsCount()
      .then(setCounts)
      .catch(() => {
        /* badge is best-effort */
      });
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000);
    return () => clearInterval(t);
  }, [refresh]);

  const value = useMemo(() => ({ counts, refresh }), [counts, refresh]);
  return <NotifContext.Provider value={value}>{children}</NotifContext.Provider>;
}

// The topbar bell's side panel: the Notifications page's feed without leaving
// the current page (phones keep the page, via the tab bar).
function NotificationsDrawer({ onClose }: { onClose: () => void }): ReactElement {
  const { t } = useTranslation();
  const { counts, refresh } = useNotifCounts();
  const feed = useNotificationFeed(counts, refresh);
  return (
    <Drawer label={t('nav.notifications')} onClose={onClose}>
      <div className="drawer-head">
        <div className="titles">
          <h2>{feed.title}</h2>
          <div className="subtitle">{feed.subtitle}</div>
          <NotificationFilter feed={feed} />
        </div>
        <button
          className="icon-btn"
          onClick={onClose}
          data-tip={t('common.close')}
          aria-label={t('common.close')}
        >
          <Icon name="x" size={18} />
        </button>
      </div>
      <div className="drawer-body">
        <NotificationList feed={feed} />
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------- nav links -- */

// Topbar nav entries are icon-only (the label lives in the tooltip); the
// mobile tab bar keeps its text labels.
function NavItem({
  to,
  icon,
  label,
  badge,
  end,
}: {
  to: string;
  icon: IconName;
  label: string;
  badge?: number;
  end?: boolean;
}): ReactElement {
  return (
    <NavLink
      to={to}
      end={end}
      data-tip={label}
      aria-label={label}
      className={({ isActive }) => `nav-link icon-only${isActive ? ' active' : ''}`}
    >
      <Icon name={icon} size={20} />
      {!!badge && <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>}
    </NavLink>
  );
}

function TabItem({
  to,
  icon,
  label,
  badge,
  end,
}: {
  to: string;
  icon: IconName;
  label: string;
  badge?: number;
  end?: boolean;
}): ReactElement {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `tab${isActive ? ' active' : ''}`}>
      <Icon name={icon} size={22} />
      {label}
      {!!badge && <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>}
    </NavLink>
  );
}

/* ----------------------------------------------------------------- shell -- */

export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.divako_mobile_portal';
export const APP_STORE_URL = 'https://apps.apple.com/app/divako/id6449188291';

function Logo(): ReactElement {
  // Two variants so the wordmark stays legible on both themes; index.css
  // swaps them by [data-theme].
  return (
    <NavLink to="/" className="topbar-logo" aria-label="Divako Portal">
      <img className="only-dark" src="/logo_white_green.svg" alt="Divako" />
      <img className="only-light" src="/logo-light-bg.svg" alt="Divako" />
    </NavLink>
  );
}

// Smart-app-banner for phones: app icon + install link for the visitor's
// store, dismissable (remembered on this device). Sits above the topbar and
// scrolls away with the page, like the native iOS banner.
function AppInstallBanner(): ReactElement | null {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem('appBannerDismissed') === '1';
    } catch {
      return true;
    }
  });

  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  if (dismissed || (!isIOS && !isAndroid)) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem('appBannerDismissed', '1');
    } catch {
      /* best effort */
    }
  };

  return (
    <div className="app-banner">
      <button className="icon-btn" onClick={dismiss} aria-label={t('common.close')}>
        <Icon name="x" size={16} />
      </button>
      <img className="app-banner-icon" src="/logo_short_white.svg" alt="" />
      <div className="app-banner-text">
        <strong>{t('ui.getApp')}</strong>
        <span>{isIOS ? 'App Store' : 'Google Play'}</span>
      </div>
      <a
        className="btn btn-primary"
        href={isIOS ? APP_STORE_URL : PLAY_STORE_URL}
        target="_blank"
        rel="noreferrer"
      >
        {t('ui.install')}
      </a>
    </div>
  );
}

// Group/category picker — centered in the topbar (the legacy portal's
// pattern). Selecting a group navigates home with it. The trigger is the
// mobile app's group bar: the entry, its project small under it, and the
// switch arrows.
function GroupPicker(): ReactElement | null {
  const { t } = useTranslation();
  const { groups, group, selectGroup } = useSelectedGroup();
  const groupName = useGroupName();
  if (!group) return null;
  return (
    <Dropdown
      trigger={(open) => (
        <button
          className="dropdown-trigger"
          aria-expanded={open}
          aria-label={`${t('home.selectGroup', { context: wordingFor(group.project_type) })}: ${groupName(group)}, ${group.project_name}`}
        >
          <span className="trigger-lines">
            <span className="trigger-text">{groupName(group)}</span>
            {group.project_name && <span className="trigger-sub">{group.project_name}</span>}
          </span>
          <Icon name="swap" size={18} />
        </button>
      )}
    >
      {groups.map((g) => (
        <button
          key={groupKey(g)}
          className={`menu-item${g === group ? ' selected' : ''}`}
          onClick={() => selectGroup(g)}
        >
          <div className="menu-item-body">
            <div className="menu-item-title">{groupName(g)}</div>
            <div className="menu-item-sub">{g.project_name}</div>
          </div>
          {g === group && <Icon name="check" size={16} />}
        </button>
      ))}
    </Dropdown>
  );
}

// Accessibility menu — the always-visible entry point for the reading aids
// (eesti.ee-style): text size, high contrast, reduced motion. The same
// settings also live on the profile page (the only surface on phones, where
// the topbar collapses). Device-level, persisted by lib/a11y.tsx.
function A11yMenu(): ReactElement {
  const { t } = useTranslation();
  const { settings, active, update } = useA11y();
  return (
    <Dropdown
      align="right"
      closeOnClick={false}
      trigger={(open) => (
        <button
          className={`icon-btn${active ? ' active' : ''}`}
          aria-expanded={open}
          data-tip={t('a11y.title')}
          aria-label={t('a11y.title')}
        >
          <Icon name="accessibility" size={20} />
        </button>
      )}
    >
      <div className="a11y-menu" role="group" aria-label={t('a11y.title')}>
        <div className="a11y-title">
          <Icon name="accessibility" size={18} />
          {t('a11y.title')}
        </div>
        <div className="a11y-row">
          <div className="a11y-row-text">
            <div className="a11y-row-label">{t('a11y.textSize')}</div>
          </div>
          <Segmented
            options={[
              { value: 'normal' as TextSize, label: 'A', ariaLabel: t('a11y.textSizeNormal') },
              { value: 'large' as TextSize, label: 'A+', ariaLabel: t('a11y.textSizeLarge') },
              { value: 'xlarge' as TextSize, label: 'A++', ariaLabel: t('a11y.textSizeXlarge') },
            ]}
            value={settings.textSize}
            onChange={(v) => update({ textSize: v })}
          />
        </div>
        <div className="a11y-row">
          <div className="a11y-row-text">
            <div className="a11y-row-label">{t('a11y.highContrast')}</div>
            <div className="a11y-row-help">{t('a11y.highContrastHelp')}</div>
          </div>
          <Switch
            checked={settings.highContrast}
            ariaLabel={t('a11y.highContrast')}
            onChange={(v) => update({ highContrast: v })}
          />
        </div>
        <div className="a11y-row">
          <div className="a11y-row-text">
            <div className="a11y-row-label">{t('a11y.reduceMotion')}</div>
            <div className="a11y-row-help">{t('a11y.reduceMotionHelp')}</div>
          </div>
          <Switch
            checked={settings.reduceMotion}
            ariaLabel={t('a11y.reduceMotion')}
            onChange={(v) => update({ reduceMotion: v })}
          />
        </div>
        <div className="a11y-hint">{t('a11y.hint')}</div>
      </div>
    </Dropdown>
  );
}

// Avatar in the topbar — this IS the profile button (no separate nav item).
// Falls back to a person icon if the avatar image can't load, so the profile
// stays reachable.
function AvatarLink({ uid, label }: { uid: string; label: string }): ReactElement {
  const [broken, setBroken] = useState(false);
  return (
    <NavLink
      to="/profile"
      aria-label={label}
      data-tip={label}
      className={({ isActive }) => `avatar-link${isActive ? ' active' : ''}`}
    >
      {broken ? (
        <span className="avatar avatar-sm avatar-fallback">
          <Icon name="user" size={16} />
        </span>
      ) : (
        <img
          className="avatar avatar-sm"
          src={avatarUrl(uid)}
          alt=""
          onError={() => setBroken(true)}
        />
      )}
    </NavLink>
  );
}

function ShellChrome(): ReactElement {
  const { t } = useTranslation();
  const { me } = useUser();
  const { counts } = useNotifCounts();
  const { mode, setPreference } = useTheme();
  const [notifOpen, setNotifOpen] = useState(false);
  const closeNotif = useCallback(() => setNotifOpen(false), []);
  const unread = counts?.unread ?? 0;

  const home = { to: '/', icon: 'home' as IconName, label: t('nav.home'), end: true };
  // Billing is a project setting, offered to the owners of a home that has it
  // (the mobile tab's rule).
  const billing = hasBilling(me?.categories_and_groups ?? [])
    ? { to: '/billing', icon: 'receipt' as IconName, label: t('nav.billing') }
    : null;
  const items = [
    home,
    ...(billing ? [billing] : []),
    {
      to: '/notifications',
      icon: 'bell' as IconName,
      label: t('nav.notifications'),
      badge: unread,
    },
    { to: '/profile', icon: 'user' as IconName, label: t('nav.profile') },
  ];

  // Topbar: Home, Billing where the user has it, the notifications bell
  // (opens the drawer in place rather than navigating) and the avatar (which
  // is the profile button). The mobile tab bar keeps them all as page links —
  // it has no avatar or drawer.
  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('a11y.skipToContent')}
      </a>
      <AppInstallBanner />
      <header className="topbar">
        <div className="topbar-inner">
          <Logo />
          <div className="topbar-center">
            <GroupPicker />
          </div>
          <nav className="topbar-nav">
            <NavItem {...home} />
            {billing && <NavItem {...billing} />}
            <button
              className={`icon-btn${notifOpen ? ' active' : ''}`}
              onClick={() => setNotifOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={notifOpen}
              data-tip={t('nav.notifications')}
              aria-label={t('nav.notifications')}
            >
              <Icon name="bell" size={20} />
              {!!unread && <span className="nav-badge">{unread > 99 ? '99+' : unread}</span>}
            </button>
            <A11yMenu />
            <button
              className="icon-btn"
              onClick={(e) =>
                revealTheme(e.currentTarget, () => setPreference(mode === 'dark' ? 'light' : 'dark'))
              }
              data-tip={t('profile.appearance')}
              aria-label={t('profile.appearance')}
            >
              <Icon name={mode === 'dark' ? 'sun' : 'moon'} size={19} />
            </button>
            {me && <AvatarLink uid={me.uid} label={t('nav.profile')} />}
          </nav>
        </div>
      </header>

      <main className="content" id="main" tabIndex={-1}>
        <Outlet />
      </main>

      <footer className="page-footer">
        <span>{t('profile.version', { version: config.appVersion })} · Divako ©</span>
        {/* Same store links as the legacy portal footer. */}
        <span className="store-badges">
          <a
            href="https://play.google.com/store/apps/details?id=com.divako_mobile_portal"
            target="_blank"
            rel="noreferrer"
          >
            <img alt="Get it on Google Play" src="/googleplay_badge.svg" />
          </a>
          <a href="https://apps.apple.com/app/divako/id6449188291" target="_blank" rel="noreferrer">
            <img alt="Download on the App Store" src="/appstore_badge.svg" />
          </a>
        </span>
      </footer>

      <nav className="tabbar">
        {items.map((it) => (
          <TabItem key={it.to} {...it} />
        ))}
      </nav>

      {notifOpen && <NotificationsDrawer onClose={closeNotif} />}

      <ToastHost />
    </div>
  );
}

export default function AppShell(): ReactElement {
  // Any 401 from the API bounces straight back into the server-driven login
  // (same automatic-login behavior as RequireSession / the old portal).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      login(window.location.pathname + window.location.search);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  return (
    <UserProvider>
      <NotifProvider>
        <ShellChrome />
      </NotifProvider>
    </UserProvider>
  );
}
